import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from '../components/Badge';
import { EmployeeProfilePremium } from '../components/EmployeeProfilePremium';
import { EmployeeIdentityLinkPanel } from '../components/EmployeeIdentityLinkPanel';
import { enumLabel, EMPLOYEE_STATUS_VALUES, EMPLOYMENT_TYPE_VALUES } from '../utils/i18nLabels';
import { AuthorizedAction } from '../components/AuthorizedAction';
import { formatDate } from '../utils/format';

function employeeLifecycleStageLabelKey(status) {
  if (status === 'active') return 'lifecycle.stageOperational';
  if (status === 'onboarding') return 'lifecycle.stageOnboarding';
  if (status === 'offboarding') return 'lifecycle.stageOffboarding';
  return 'lifecycle.stageInactive';
}

function employeeLifecycleStageVariant(status) {
  if (status === 'active') return 'active';
  if (status === 'onboarding') return 'onboarding';
  if (status === 'offboarding') return 'offboarding';
  return 'inactive';
}

function employeeLifecycleProgress(emp) {
  if (emp.status !== 'onboarding' && emp.status !== 'offboarding') return null;
  const raw = `${emp.id}${emp.status}`;
  let n = 0;
  for (let i = 0; i < raw.length; i++) n += raw.charCodeAt(i);
  return 20 + (n % 75);
}

function documentProgressPercent(emp) {
  const p = employeeLifecycleProgress(emp);
  if (p != null) return p;
  const raw = `${emp.id}${emp.area || ''}`;
  let n = 0;
  for (let i = 0; i < raw.length; i++) n += raw.charCodeAt(i);
  return 88 + (n % 12);
}

const TABLE_COL_SPAN = 11;

const emptyForm = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  area: '',
  department: '',
  position: '',
  status: 'active',
  employment_type: 'full_time',
  location: '',
};

export function Employees() {
  const { t, canCreate, canEdit, canDelete } = useApp();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [profilePremium, setProfilePremium] = useState(null);
  const [activity, setActivity] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/employees');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api
      .get('/api/employees/activity/recent')
      .then((r) => setActivity(Array.isArray(r.data) ? r.data : []))
      .catch(() => setActivity([]));
  }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        `${r.first_name} ${r.last_name}`.toLowerCase().includes(s) ||
        (r.email && r.email.toLowerCase().includes(s)) ||
        (r.area && r.area.toLowerCase().includes(s))
    );
  }, [rows, q]);

  const metrics = useMemo(() => {
    const active = rows.filter((r) => r.status === 'active').length;
    const onboarding = rows.filter((r) => r.status === 'onboarding').length;
    const reportManagerIds = new Set(rows.map((r) => r.manager_id).filter(Boolean));
    const managers = rows.filter((r) => reportManagerIds.has(r.id)).length;
    const areas = new Set(rows.map((r) => r.area).filter(Boolean)).size;
    return { active, onboarding, managers, areas };
  }, [rows]);

  const managerById = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      m[r.id] = `${r.first_name} ${r.last_name}`;
    });
    return m;
  }, [rows]);

  function openCreate() {
    if (!canCreate('employees')) return;
    setForm(emptyForm);
    setModal('create');
  }

  function openEdit(emp) {
    if (!canEdit('employees')) return;
    setForm({
      first_name: emp.first_name,
      last_name: emp.last_name,
      email: emp.email,
      phone: emp.phone || '',
      area: emp.area || '',
      department: emp.department || '',
      position: emp.position || '',
      status: emp.status,
      employment_type: emp.employment_type,
      location: emp.location || '',
    });
    setModal({ mode: 'edit', id: emp.id });
  }

  async function save() {
    const ok =
      modal === 'create' ? canCreate('employees') : modal?.mode === 'edit' ? canEdit('employees') : false;
    if (!ok) return;
    try {
      if (modal === 'create') {
        await api.post('/api/employees', form);
      } else if (modal?.mode === 'edit') {
        await api.put(`/api/employees/${modal.id}`, form);
      }
      setModal(null);
      await load();
    } catch {
      /* MVP: silent */
    }
  }

  async function remove(id) {
    if (!canDelete('employees')) return;
    if (!window.confirm('OK?')) return;
    try {
      await api.delete(`/api/employees/${id}`);
      await load();
    } catch {
      /* silent */
    }
  }

  const permEdit = canEdit('employees');
  const permDelete = canDelete('employees');
  const canSaveModal =
    modal === 'create' ? canCreate('employees') : modal && modal.mode === 'edit' ? permEdit : false;

  return (
    <div className="page-stack page-stack--employees">
      <div className="employees-page-head">
        <div>
          <p className="page-lead page-lead--tight">{t('employees.subtitle')}</p>
        </div>
        <div className="employees-page-head__actions">
          <AuthorizedAction module="employees" action="create" className="btn btn--primary btn--employees-new" onClick={openCreate}>
            {t('employees.new')}
          </AuthorizedAction>
        </div>
      </div>

      <div className="employees-summary-cards">
        <div className="employees-summary-card employees-summary-card--accent">
          <span className="employees-summary-card__value">{metrics.active}</span>
          <span className="employees-summary-card__label">{t('employees.summaryActive')}</span>
        </div>
        <div className="employees-summary-card">
          <span className="employees-summary-card__value">{metrics.onboarding}</span>
          <span className="employees-summary-card__label">{t('employees.summaryOnboarding')}</span>
        </div>
        <div className="employees-summary-card">
          <span className="employees-summary-card__value">{metrics.managers}</span>
          <span className="employees-summary-card__label">{t('employees.summaryManagers')}</span>
        </div>
        <div className="employees-summary-card">
          <span className="employees-summary-card__value">{metrics.areas}</span>
          <span className="employees-summary-card__label">{t('employees.summaryAreas')}</span>
        </div>
      </div>

      <div className="toolbar toolbar--card toolbar--employees">
        <input
          className="input input--search"
          placeholder={t('employees.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <input className="input input--filters" type="text" readOnly value="" placeholder={t('employees.filtersPlaceholder')} />
      </div>

      <div className="table-wrap table-wrap--rounded">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('employees.columns.name')}</th>
              <th className="hide-sm">{t('employees.columns.email')}</th>
              <th>{t('employees.columns.area')}</th>
              <th className="hide-sm">{t('employees.columns.manager')}</th>
              <th className="hide-sm">{t('employees.columns.tenure')}</th>
              <th>{t('employees.columns.status')}</th>
              <th className="hide-sm">{t('employees.columns.position')}</th>
              <th className="hide-sm">{t('employees.columns.legajo')}</th>
              <th>{t('employees.columns.lifecycle')}</th>
              <th className="hide-sm">{t('employees.columns.progress')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COL_SPAN} className="empty-inline">
                  {t('common.loading')}
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COL_SPAN} className="empty-inline">
                  {t('employees.empty')}
                </td>
              </tr>
            ) : (
              filtered.map((emp) => {
                const prog = documentProgressPercent(emp);
                const mgr = emp.manager_id ? managerById[emp.manager_id] : null;
                return (
                <tr key={emp.id} className="employees-row-premium">
                  <td>
                    <strong>
                      {emp.first_name} {emp.last_name}
                    </strong>
                    <div className="text-muted text-sm hide-md-up">{emp.email}</div>
                  </td>
                  <td className="hide-sm">{emp.email}</td>
                  <td>{emp.area || '—'}</td>
                  <td className="hide-sm">{mgr || '—'}</td>
                  <td className="hide-sm">{emp.start_date ? formatDate(emp.start_date) : '—'}</td>
                  <td>
                    <Badge variant={emp.status}>{enumLabel(t, 'employeeStatus', emp.status)}</Badge>
                  </td>
                  <td className="hide-sm">{emp.position || '—'}</td>
                  <td className="hide-sm">
                    {emp.employee_code || emp.document_id ? (
                      <span className="legajo-pill">{t('employees.legajoHint')}</span>
                    ) : (
                      <span className="text-muted text-sm">—</span>
                    )}
                  </td>
                  <td>
                    <Badge variant={employeeLifecycleStageVariant(emp.status)}>{t(employeeLifecycleStageLabelKey(emp.status))}</Badge>
                  </td>
                  <td className="hide-sm">
                    <span className="employees-progress-pill">{prog}%</span>
                  </td>
                  <td>
                    <div className="row-actions row-actions--perm">
                      <Link to={`/records?employee_id=${emp.id}`} className="btn btn--sm btn--secondary">
                        {t('employees.viewLegajo')}
                      </Link>
                      <Link to={`/employee-lifecycle?employee=${encodeURIComponent(emp.id)}`} className="btn btn--sm btn--secondary">
                        {t('employees.viewLifecycle')}
                      </Link>
                      <Link to={`/attendance?employee_id=${encodeURIComponent(emp.id)}`} className="btn btn--sm btn--secondary">
                        {t('employees.quickAttendance')}
                      </Link>
                      <Link to="/performance" className="btn btn--sm btn--secondary">
                        {t('employees.quickEval')}
                      </Link>
                      <button
                        type="button"
                        className="btn btn--sm btn--secondary"
                        onClick={() => setProfilePremium(emp)}
                      >
                        {t('employees.viewProfilePremium')}
                      </button>
                      <span className="row-actions__perm-wrap">
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost"
                          onClick={() => openEdit(emp)}
                          disabled={!permEdit}
                        >
                          {t('employees.edit')}
                        </button>
                        {!permEdit && <span className="perm-req-badge perm-req-badge--inline">{t('permissions.requirePermBadge')}</span>}
                      </span>
                      <span className="row-actions__perm-wrap">
                        <button
                          type="button"
                          className="btn btn--sm btn--danger"
                          onClick={() => remove(emp.id)}
                          disabled={!permDelete}
                        >
                          {t('employees.delete')}
                        </button>
                        {!permDelete && <span className="perm-req-badge perm-req-badge--inline">{t('permissions.requirePermBadge')}</span>}
                      </span>
                    </div>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      <section className="panel panel--elevated employees-timeline">
        <h2 className="panel__title">{t('employees.timelineTitle')}</h2>
        {activity.length === 0 ? (
          <p className="panel__muted text-sm">{t('employees.timelineEmpty')}</p>
        ) : (
          <ol className="employees-timeline__list">
            {activity.map((ev) => (
              <li key={ev.id}>
                <time className="text-sm panel__muted">{formatDate(ev.created_at)}</time> — {ev.summary || ev.action}
              </li>
            ))}
          </ol>
        )}
      </section>

      {profilePremium && (
        <div className="modal-backdrop" role="dialog" aria-modal aria-labelledby="emp-profile-premium-title">
          <div className="modal modal--wide emp-profile-premium-modal">
            <EmployeeProfilePremium
              employee={profilePremium}
              managerName={profilePremium.manager_id ? managerById[profilePremium.manager_id] : null}
              onClose={() => setProfilePremium(null)}
            />
            <EmployeeIdentityLinkPanel employee={profilePremium} />
          </div>
        </div>
      )}

      {modal && (
        <div className="modal-backdrop" role="dialog" aria-modal>
          <div className="modal">
            <h2 className="modal__title">{t('employees.modalTitle')}</h2>
            <div className="form-grid">
              <label className="field">
                <span>{t('employees.fieldFirstName')}</span>
                <input
                  className="input"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </label>
              <label className="field">
                <span>{t('employees.fieldLastName')}</span>
                <input
                  className="input"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </label>
              <label className="field field--full">
                <span>{t('employees.fieldEmail')}</span>
                <input
                  className="input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </label>
              <label className="field">
                <span>{t('employees.fieldArea')}</span>
                <input className="input" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
              </label>
              <label className="field">
                <span>{t('employees.fieldDepartment')}</span>
                <input
                  className="input"
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                />
              </label>
              <label className="field field--full">
                <span>{t('employees.fieldPosition')}</span>
                <input
                  className="input"
                  value={form.position}
                  onChange={(e) => setForm({ ...form, position: e.target.value })}
                />
              </label>
              <label className="field">
                <span>{t('employees.fieldStatus')}</span>
                <select
                  className="input"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {EMPLOYEE_STATUS_VALUES.map((s) => (
                    <option key={s} value={s}>
                      {enumLabel(t, 'employeeStatus', s)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>{t('employees.fieldEmploymentType')}</span>
                <select
                  className="input"
                  value={form.employment_type}
                  onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
                >
                  {EMPLOYMENT_TYPE_VALUES.map((s) => (
                    <option key={s} value={s}>
                      {enumLabel(t, 'employmentType', s)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setModal(null)}>
                {t('employees.cancel')}
              </button>
              <div className="modal__actions-primary">
                <button type="button" className="btn btn--primary" onClick={save} disabled={!canSaveModal}>
                  {t('employees.save')}
                </button>
                {!canSaveModal && <span className="perm-req-badge">{t('permissions.requirePermBadge')}</span>}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
